import { BrowserRouter, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { UploadForm } from './components/UploadForm';
import { ResultsEnhanced } from './components/Results-Enhanced';
import { ScanViewer } from './components/ScanViewer';
import { HistoryPage } from './components/HistoryPage';
import { LoginModal } from './components/LoginModal';
import { SideBySideCompare } from './components/SideBySideCompare';
import { PreviewGrid } from './components/PreviewGrid';
import { OptimizationToolbox } from './components/OptimizationToolbox';
import { TeamManager } from './components/TeamManager';
import { WebhookManager } from './components/WebhookManager';
import { AdminDashboard } from './components/AdminDashboard';
import { TagTesterIntake } from './components/TagTesterIntake';
import { TagTesterResults } from './components/TagTesterResults';
import { TagTesterHistory } from './components/TagTesterHistory';
import { useTheme } from './components/ThemeProvider';
import { useToast } from './components/Toast';
import { ScanResult } from './types';
import { apiUrl } from './config/api';
import { csrfHeaders } from './utils/csrf';
import './App.css';
import './components/Results-Enhanced.css';

function UploadPage() {
  const [result, setResult] = useState<ScanResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  const handleUpload = async (file: File, profile: string) => {
    setLoading(true);
    setError(null);
    setResult(null);

    const formData = new FormData();
    formData.append('file', file);

    let url = apiUrl(`/upload?profile=${profile}`);
    
    const customInitial = localStorage.getItem('threshold_load-initial');
    const customSubload = localStorage.getItem('threshold_load-subload');
    const customFiles = localStorage.getItem('threshold_hosted-files');
    const customZip = localStorage.getItem('threshold_compressed-size');
    
    if (customInitial) {
      const match = customInitial.match(/(\d+)/);
      if (match) url += `&initialKbMax=${match[1]}`;
    }
    if (customSubload) {
      const match = customSubload.match(/(\d+)/);
      if (match) url += `&subloadKbMax=${match[1]}`;
    }
    if (customFiles) {
      const match = customFiles.match(/(\d+)/);
      if (match) url += `&filesMax=${match[1]}`;
    }
    if (customZip) {
      const match = customZip.match(/(\d+)/);
      if (match) url += `&zipBytesMax=${parseInt(match[1]) * 1024}`;
    }

    try {
      const response = await fetch(url, {
        method: 'POST',
        body: formData,
        credentials: 'include'
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error?.message || 'Upload failed');
      }

      setResult(data.data);
      toast('Scan complete!', 'success');
    } catch (err) {
      console.error('Upload error:', err);
      const msg = err instanceof Error ? err.message : 'Upload failed';
      setError(msg);
      toast(msg, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setResult(null);
    setError(null);
  };

  return (
    <div className='app'>
      <AppHeader />

      <main className='app-main'>
        {!result && (
          <UploadForm
            onUpload={handleUpload}
            loading={loading}
            error={error}
          />
        )}

        {result && (
          <ResultsEnhanced result={result} onReset={handleReset} />
        )}
      </main>

      <footer className='app-footer'>
        <p>Supports Google and IAB validation profiles</p>
      </footer>
    </div>
  );
}

function AppHeader() {
  const navigate = useNavigate();
  const location = useLocation();
  const { theme, toggleTheme } = useTheme();
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const currentPath = location.pathname;
  
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const res = await fetch(apiUrl('/auth/me'), { credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          if (data.authenticated && data.email) {
            setUserEmail(data.email);
            const statsRes = await fetch(apiUrl('/admin/stats'), { credentials: 'include' });
            setIsAdmin(statsRes.ok);
          }
        }
      } catch {
        // Not logged in
      }
    };
    checkAuth();
  }, []);
  
  const handleLogout = async () => {
    await fetch(apiUrl('/auth/logout'), {
      method: 'POST',
      headers: csrfHeaders(),
      credentials: 'include'
    });
    setUserEmail(null);
    setIsAdmin(false);
    window.location.reload();
  };

  const navButtonStyle = (path: string) => ({
    padding: '10px 16px',
    backgroundColor: currentPath === path ? 'var(--nav-active-bg)' : 'var(--nav-inactive-bg)',
    color: currentPath === path ? 'var(--nav-active-text)' : 'var(--nav-inactive-text)',
    border: currentPath === path ? 'none' : '1px solid var(--nav-inactive-border)',
    borderRadius: '6px',
    cursor: 'pointer' as const,
    fontWeight: 500 as const,
    fontSize: '14px',
    transition: 'all 0.2s'
  });
  
  return (
    <header className='app-header'>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
        <div>
          <h1 style={{ cursor: 'pointer', margin: '0 0 5px 0' }} onClick={() => navigate('/')}>
            HTML5 Creative Validator
          </h1>
          <p style={{ margin: 0, fontSize: '14px', color: '#ffffff' }}>
            Validate your HTML5 ad creatives against industry standards
          </p>
        </div>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          <button
            onClick={toggleTheme}
            title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
            style={{
              padding: '8px 12px',
              backgroundColor: 'rgba(255,255,255,0.15)',
              color: '#fff',
              border: '1px solid rgba(255,255,255,0.3)',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '16px',
              lineHeight: 1,
            }}
          >
            {theme === 'dark' ? '\u2600' : '\u263D'}
          </button>
          {userEmail ? (
            <>
              <span style={{ fontSize: '14px', color: '#ffffff' }}>{userEmail}</span>
              <button
                onClick={handleLogout}
                style={{
                  padding: '8px 16px',
                  backgroundColor: 'rgba(255,255,255,0.15)',
                  color: '#fff',
                  border: '1px solid rgba(255,255,255,0.3)',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: 500,
                  fontSize: '14px'
                }}
              >
                Logout
              </button>
            </>
          ) : (
            <button
              onClick={() => setShowLogin(true)}
              style={{
                padding: '8px 16px',
                backgroundColor: 'rgba(255,255,255,0.2)',
                color: '#fff',
                border: '1px solid rgba(255,255,255,0.4)',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: 500,
                fontSize: '14px'
              }}
            >
              Login
            </button>
          )}
        </div>
      </div>

      <nav style={{ 
        display: 'flex', 
        gap: '10px', 
        padding: '15px 0 0 0',
        borderTop: '1px solid rgba(255,255,255,0.2)'
      }}>
        <button onClick={() => navigate('/')} style={navButtonStyle('/')}>
          Upload
        </button>
        <button onClick={() => navigate('/gallery')} style={navButtonStyle('/gallery')}>
          Gallery
        </button>
        <button onClick={() => navigate('/compare')} style={navButtonStyle('/compare')}>
          Compare
        </button>
        <button onClick={() => navigate('/optimize')} style={navButtonStyle('/optimize')}>
          Optimize
        </button>
        <button onClick={() => navigate('/history')} style={navButtonStyle('/history')}>
          History
        </button>
        <button onClick={() => navigate('/tag-tester')} style={navButtonStyle('/tag-tester')}>
          Tag Tester
        </button>
        {userEmail && (
          <>
            <button onClick={() => navigate('/teams')} style={navButtonStyle('/teams')}>
              Teams
            </button>
            <button onClick={() => navigate('/webhooks')} style={navButtonStyle('/webhooks')}>
              Webhooks
            </button>
          </>
        )}
        {isAdmin && (
          <button onClick={() => navigate('/admin')} style={navButtonStyle('/admin')}>
            Admin
          </button>
        )}
      </nav>

      {showLogin && (
        <LoginModal
          onClose={() => setShowLogin(false)}
        />
      )}
    </header>
  );
}


function NotFoundPage() {
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      justifyContent: 'center', minHeight: '60vh', textAlign: 'center',
      padding: '2rem'
    }}>
      <h1 style={{ fontSize: '4rem', marginBottom: '0.5rem', opacity: 0.3 }}>404</h1>
      <h2 style={{ fontSize: '1.5rem', marginBottom: '1rem' }}>Page Not Found</h2>
      <p style={{ marginBottom: '2rem', opacity: 0.7 }}>
        The page you are looking for does not exist or has been moved.
      </p>
      <a href="/" style={{
        padding: '0.75rem 1.5rem', borderRadius: '0.5rem',
        background: '#6366f1', color: 'white', textDecoration: 'none',
        fontWeight: 600
      }}>Back to Home</a>
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path='/' element={<UploadPage />} />
        
        <Route path='/gallery' element={
          <div className='app'>
            <AppHeader />
            <main className='app-main'>
              <PreviewGrid />
            </main>
          </div>
        } />

        <Route path='/compare' element={
          <div className='app'>
            <AppHeader />
            <main className='app-main'>
              <SideBySideCompare />
            </main>
          </div>
        } />

        <Route path='/optimize' element={
          <div className='app'>
            <AppHeader />
            <main className='app-main'>
              <OptimizationToolbox />
            </main>
          </div>
        } />

        <Route path='/history' element={
          <div className='app'>
            <AppHeader />
            <main className='app-main'>
              <HistoryPage />
            </main>
            <footer className='app-footer'>
              <p>Supports Google and IAB validation profiles</p>
            </footer>
          </div>
        } />

        <Route path='/teams' element={
          <div className='app'>
            <AppHeader />
            <main className='app-main'>
              <TeamManager />
            </main>
          </div>
        } />

        <Route path='/webhooks' element={
          <div className='app'>
            <AppHeader />
            <main className='app-main'>
              <WebhookManager />
            </main>
          </div>
        } />

        <Route path='/tag-tester' element={
          <div className='app'>
            <AppHeader />
            <main className='app-main'>
              <TagTesterIntake />
            </main>
          </div>
        } />

        <Route path='/tag-tester/history' element={
          <div className='app'>
            <AppHeader />
            <main className='app-main'>
              <TagTesterHistory />
            </main>
          </div>
        } />

        <Route path='/tag-tester/:scanId' element={
          <div className='app'>
            <AppHeader />
            <main className='app-main'>
              <TagTesterResults />
            </main>
          </div>
        } />

        <Route path='/admin' element={
          <div className='app'>
            <AppHeader />
            <main className='app-main'>
              <AdminDashboard />
            </main>
          </div>
        } />
        
        <Route path='/scans/:scanId' element={
          <div className='app'>
            <AppHeader />
            <main className='app-main'>
              <ScanViewer />
            </main>
            <footer className='app-footer'>
              <p>Supports Google and IAB validation profiles</p>
            </footer>
          </div>
        } />
                <Route path='*' element={<NotFoundPage />} />
          </Routes>
    </BrowserRouter>
  );
}

export default App;
